import { CalendarDays, Map, MessageCircle, Plus, User, type LucideIcon } from 'lucide-react';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { TAB_PATHS, type Tab } from '../../app/paths';
import { useNewChatIds } from '../../features/chat/use-my-chats';

const icons: Record<Tab, LucideIcon> = {
	map: Map,
	activities: CalendarDays,
	create: Plus,
	chats: MessageCircle,
	profile: User,
};

// How many mounted views asked to hide the tab bar: open sheets (T-1302) and the chat (T-1304).
let hiders = 0;
const listeners = new Set<() => void>();
const setHiders = (next: number) => {
	hiders = next;
	for (const listener of listeners) listener();
};
const subscribe = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

/** Hides the tab bar while the calling component is mounted and `hide` is true. */
export function useHideTabBar(hide = true): void {
	useEffect(() => {
		if (!hide) return;
		setHiders(hiders + 1);
		return () => {
			setHiders(hiders - 1);
		};
	}, [hide]);
}

/** True while something asked to hide the tab bar. */
export function useTabBarHidden(): boolean {
	return useSyncExternalStore(subscribe, () => hiders > 0);
}

// Survives the bar hiding and coming back: each new chat pulses the icon once per app open.
const pulsedChats = new Set<string>();

/** The Chats icon: a count of never-opened chats, one pulse when a new one arrives (T-1335). */
function ChatsIcon() {
	const { t } = useTranslation();
	const ids = useNewChatIds();
	const [pulses, setPulses] = useState(0);
	useEffect(() => {
		const fresh = ids.filter((id) => !pulsedChats.has(id));
		if (fresh.length === 0) return;
		for (const id of fresh) pulsedChats.add(id);
		setPulses((count) => count + 1);
	}, [ids]);
	return (
		<span
			key={pulses}
			className={`relative ${pulses > 0 ? 'animate-tab-pulse motion-reduce:animate-none' : ''}`}
		>
			<MessageCircle aria-hidden="true" className="size-6" strokeWidth={2} />
			{ids.length > 0 && (
				<span
					role="status"
					aria-label={t('chats.new_chats', { count: ids.length })}
					className="bg-primary text-text absolute -top-1 -right-2 flex size-4 items-center justify-center rounded-full text-[10px] font-semibold"
				>
					{ids.length > 9 ? '9+' : ids.length}
				</span>
			)}
		</span>
	);
}

/** Bottom tabs: Карта · Активності · ＋Створити · Чати · Профіль (SCREENS → Navigation, T-306a). */
export function TabBar() {
	const { t } = useTranslation();
	return (
		<nav
			aria-label={t('app.name')}
			// `relative z-10`: the raised «+» sticks out above the bar; positioned screens (the map)
			// would otherwise paint over it.
			className="border-border bg-bg/95 pb-safe-bottom relative z-10 grid grid-cols-5 border-t"
		>
			{(Object.keys(TAB_PATHS) as Tab[]).map((tab) => {
				const Icon = icons[tab];
				return (
					<NavLink
						key={tab}
						to={TAB_PATHS[tab]}
						className={({ isActive }) =>
							`flex min-h-14 flex-col items-center justify-end gap-1 pb-1.5 text-caption ${
								isActive ? 'text-primary' : 'text-text-muted'
							}`
						}
					>
						{tab === 'create' ? (
							<span className="bg-primary text-text shadow-primary/40 -mt-5 flex size-13 items-center justify-center rounded-full shadow-lg ring-4 ring-bg">
								<Icon aria-hidden="true" className="size-7" strokeWidth={2.5} />
							</span>
						) : tab === 'chats' ? (
							<ChatsIcon />
						) : (
							<Icon aria-hidden="true" className="size-6" strokeWidth={2} />
						)}
						<span>{t(`tabs.${tab}`)}</span>
					</NavLink>
				);
			})}
		</nav>
	);
}
